/**
 * PickHistoryList Component
 * FlashList with infinite scroll for pick history.
 * Active and Match History sections are collapsible.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, ActivityIndicator, TouchableOpacity, Pressable } from 'react-native';
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { ChevronUp, ChevronDown } from 'lucide-react-native';
import { FlashList } from '@shopify/flash-list';
import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../../src/shared/constants/flashList.constants';
import { FriendStatsBanner } from './FriendStatsBanner';
import { InfoTooltip } from '../../../src/shared/components/InfoTooltip';
import { ActiveChallengesSection } from './ActiveChallengesSection';
import { MatchResultCard } from './MatchResultCard';
import { useUserAvatar } from '../../../src/avatars/hooks/useUserAvatar';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import type { FriendStatsSummary, PickHistoryEntry } from '../../../src/friends/types/friends.types';
import { CHALLENGE_STATUS } from '../../../src/friends/constants/challengeStatus.constants';
import { getHitRateFromPickHistory } from '../../../src/friends/helpers/friendStatsHitRate.helper';
import { getPickHistoryListStyles, getCoinDeltaTextStyle } from './PickHistoryList.styles';
import { getAssetUrl, ASSET_PATHS } from '../../../constants/assets'
import { COINS_BALANCE_TOOLTIP } from '../../../constants/tooltips';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { useTheme } from '../../../src/theme/useTheme';

const COIN_150_URI = getAssetUrl(ASSET_PATHS.COINS.COIN_150);

interface PickHistoryListProps {
  summary: FriendStatsSummary;
  pickHistory: PickHistoryEntry[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** When true, banner runs count-up animation; when false (e.g. after refresh), shows final values immediately */
  shouldAnimateBanner?: boolean;
  /** When set, show a low-emphasis "Remove friend" action at the bottom (1v1 history only) */
  onRemoveFriend?: (friendId: string) => void;
  /** When set, empty state can show "Challenge {friend}" button (1v1 history only) */
  onChallengeFriend?: () => void;
  /** Test ID for testing */
  testID?: string;
}

const SECTION_ACTIVE = 'Active';
const SECTION_MATCH_HISTORY = 'Match History';

export const PickHistoryList: React.FC<PickHistoryListProps> = ({
  summary,
  pickHistory,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  shouldAnimateBanner = true,
  onRemoveFriend,
  onChallengeFriend,
  testID,
}) => {
  const theme = useTheme();
  const friendStatsStyles = useMemo(() => getPickHistoryListStyles(theme), [theme]);
  const fetchedUserAvatar = useUserAvatar();
  const { user: currentUser } = useSupabaseAuth();
  const [activeExpanded, setActiveExpanded] = useState(true);
  const [matchHistoryExpanded, setMatchHistoryExpanded] = useState(true);
  // Prefer live profile (refetched on focus) so avatar updates when changed in menu; fallback to summary to avoid flash on first load
  const userAvatarId = fetchedUserAvatar ?? summary.user?.avatarId;

  // Separate active and completed challenges; compute hit rates. Coin delta comes from server summary (all challenges, not just loaded page).
  const { activeChallenges, completedChallenges, hitRate } = useMemo(() => {
    const active = pickHistory.filter((entry) => entry.status === CHALLENGE_STATUS.ACTIVE);
    const completed = pickHistory.filter((entry) => entry.status === CHALLENGE_STATUS.COMPLETED);
    const hitRate = getHitRateFromPickHistory(pickHistory);
    return { activeChallenges: active, completedChallenges: completed, hitRate };
  }, [pickHistory]);
  const totalCoinDelta = summary.coinsDelta ?? 0;

  const toggleActive = useCallback(() => setActiveExpanded((prev) => !prev), []);
  const toggleMatchHistory = useCallback(() => setMatchHistoryExpanded((prev) => !prev), []);

  const renderItem = useCallback(({ item }: { item: PickHistoryEntry }) => {
    const creatorName = item.creatorId
      ? item.creatorId === currentUser?.id ? 'You' : summary.friend.name
      : null;
    return (
      <MatchResultCard
        data={item}
        friendName={summary.friend.name}
        userAvatarId={userAvatarId}
        userAvatarConfig={summary.user?.avatarConfig}
        friendAvatarId={summary.friend.avatarId ?? undefined}
        friendAvatarConfig={summary.friend.avatarConfig}
        friendFallbackUrl={summary.friend.avatar ?? undefined}
        creatorName={creatorName}
      />
    );
  }, [currentUser?.id, summary.friend, summary.user?.avatarConfig, userAvatarId]);

  const hasCoinChallenges = summary.coinsDelta != null || completedChallenges.some((e) => e.stakeCoins)
  const coinDeltaColor = totalCoinDelta > 0 ? theme.success : totalCoinDelta < 0 ? theme.error : theme.gray[500]
  const coinDeltaLabel =
    totalCoinDelta > 0
      ? `+${totalCoinDelta.toLocaleString()}`
      : totalCoinDelta.toLocaleString()
  const coinFontSize = coinDeltaLabel.length > 5 ? Math.round(44 * 0.8) : 44

  const renderHeader = () => (
    <>
      {hasCoinChallenges && (
        <LinearGradient
          colors={
            totalCoinDelta > 0
              ? [`${theme.success}1F`, `${theme.success}0A`, 'transparent']
              : totalCoinDelta < 0
                ? [`${theme.error}1F`, `${theme.error}0A`, 'transparent']
                : [`${theme.gray[500]}14`, 'transparent', 'transparent']
          }
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          style={friendStatsStyles.coinGradient}
        >
          {/* Header row: spacer | centered label | spacer with tooltip */}
          <View style={friendStatsStyles.coinHeaderRow}>
            <View style={friendStatsStyles.coinHeaderSpacer} />
            <Text style={friendStatsStyles.coinHeaderLabel}>Coins Balance</Text>
            <View style={friendStatsStyles.coinTooltipSpacer}>
              <InfoTooltip
                content={COINS_BALANCE_TOOLTIP}
                accessibilityLabel="What is Coins Balance?"
                compact
              />
            </View>
          </View>

          {/* Number centered, coin sits just to its right */}
          <View style={friendStatsStyles.coinValueOuter}>
            <View style={friendStatsStyles.coinValueRow}>
              <View style={friendStatsStyles.coinValueSpacer} />
              <Text style={getCoinDeltaTextStyle(coinDeltaColor, coinFontSize)}>
                {coinDeltaLabel}
              </Text>
              <Image
                source={{ uri: COIN_150_URI }}
                style={friendStatsStyles.coinImage}
                contentFit="contain"
                alt={WEB_IMAGE_ALT_DECORATIVE}
              />
            </View>
          </View>
        </LinearGradient>
      )}
      <FriendStatsBanner
        summary={summary}
        hitRate={hitRate}
        shouldAnimate={shouldAnimateBanner}
      />
      {activeChallenges.length > 0 && (
        <>
          <SoundPressable
            tapSoundId={
              activeExpanded
                ? SOUND_IDS.UI_SECTION_COLLAPSE
                : SOUND_IDS.UI_SECTION_EXPAND
            }
            onPress={toggleActive}
            style={[
              friendStatsStyles.sectionHeaderTouchable,
              !activeExpanded && friendStatsStyles.sectionHeaderTouchableCollapsed,
            ]}
            accessibilityRole="button"
            accessibilityLabel={activeExpanded ? 'Collapse Active section' : 'Expand Active section'}
          >
            <Text style={friendStatsStyles.sectionHeaderTitle}>{SECTION_ACTIVE}</Text>
            <View style={friendStatsStyles.sectionHeaderRightRow}>
              {!activeExpanded && (
                <Text style={friendStatsStyles.sectionHeaderSubtext}>Tap to expand</Text>
              )}
              {activeExpanded
                ? <ChevronUp size={24} color={theme.gray[700]} style={friendStatsStyles.sectionHeaderChevron} />
                : <ChevronDown size={24} color={theme.gray[700]} style={friendStatsStyles.sectionHeaderChevronCollapsed} />
              }
            </View>
          </SoundPressable>
          {activeExpanded && (
            <ActiveChallengesSection
              activeChallenges={activeChallenges}
              friendName={summary.friend.name}
              userAvatarId={userAvatarId}
              userAvatarConfig={summary.user?.avatarConfig}
              friendAvatarId={summary.friend.avatarId ?? undefined}
              friendAvatarConfig={summary.friend.avatarConfig}
              friendFallbackUrl={summary.friend.avatar ?? undefined}
            />
          )}
        </>
      )}
      <SoundPressable
        tapSoundId={
          matchHistoryExpanded
            ? SOUND_IDS.UI_SECTION_COLLAPSE
            : SOUND_IDS.UI_SECTION_EXPAND
        }
        onPress={toggleMatchHistory}
        style={[
          friendStatsStyles.sectionHeaderTouchable,
          !matchHistoryExpanded && friendStatsStyles.sectionHeaderTouchableCollapsed,
        ]}
        accessibilityRole="button"
        accessibilityLabel={matchHistoryExpanded ? 'Collapse Match History section' : 'Expand Match History section'}
      >
        <Text style={friendStatsStyles.sectionHeaderTitle}>{SECTION_MATCH_HISTORY}</Text>
        <View style={friendStatsStyles.sectionHeaderRightRow}>
          {!matchHistoryExpanded && (
            <Text style={friendStatsStyles.sectionHeaderSubtext}>Tap to expand</Text>
          )}
          {matchHistoryExpanded
            ? <ChevronUp size={24} color={theme.gray[700]} style={friendStatsStyles.sectionHeaderChevron} />
            : <ChevronDown size={24} color={theme.gray[700]} style={friendStatsStyles.sectionHeaderChevronCollapsed} />
          }
        </View>
      </SoundPressable>
    </>
  );

  const renderFooter = () => (
    <>
      {matchHistoryExpanded && hasNextPage && (
        isFetchingNextPage ? (
          <View style={friendStatsStyles.listFooter}>
            <ActivityIndicator size="small" color={theme.primary} />
          </View>
        ) : (
          <View style={friendStatsStyles.listFooter}>
            <TouchableOpacity
              onPress={() => fetchNextPage()}
              style={friendStatsStyles.viewMoreButton}
              accessibilityRole="button"
              accessibilityLabel="Load 10 more challenges"
            >
              <Text style={friendStatsStyles.viewMoreButtonText}>View 10 more</Text>
            </TouchableOpacity>
          </View>
        )
      )}
      {onRemoveFriend && (
        <View style={friendStatsStyles.removeFriendFooter}>
          <Pressable
            onPress={() => onRemoveFriend(summary.friend.id)}
            style={friendStatsStyles.removeFriendButton}
            accessibilityRole="button"
            accessibilityLabel="Remove friend"
          >
            <Text style={friendStatsStyles.removeFriendButtonText}>Remove friend</Text>
          </Pressable>
        </View>
      )}
    </>
  );

  const renderEmpty = () => {
    if (summary.totalChallenges === 0) {
      return (
        <View style={friendStatsStyles.emptyContainer}>
          <Text style={friendStatsStyles.emptyTitle}>
            No challenges with {summary.friend.name} yet
          </Text>
          <Text style={friendStatsStyles.emptySubtitle}>
            Start your first 1v1 to see your history here
          </Text>
          {onChallengeFriend && (
            <TouchableOpacity
              onPress={onChallengeFriend}
              style={friendStatsStyles.challengeButton}
              accessibilityRole="button"
              accessibilityLabel={`Challenge ${summary.friend.name}`}
            >
              <Text style={friendStatsStyles.challengeButtonText}>
                Challenge {summary.friend.name}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }
    if (activeChallenges.length > 0 && completedChallenges.length === 0) {
      return (
        <View style={friendStatsStyles.emptyContainer}>
          <Text style={friendStatsStyles.emptyTitle}>Finish your first 1v1 to see your history!</Text>
          <Text style={friendStatsStyles.emptySubtitle}>Complete an active challenge with {summary.friend.name}</Text>
        </View>
      );
    }
    return (
      <View style={friendStatsStyles.emptyContainer}>
        <Text style={friendStatsStyles.emptyTitle}>No finished challenges yet</Text>
        <Text style={friendStatsStyles.emptySubtitle}>Keep playing to see your results here</Text>
      </View>
    );
  };

  const listData = matchHistoryExpanded ? completedChallenges : [];

  return (
    <FlashList
      overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
      data={listData}
      renderItem={renderItem}
      keyExtractor={(item) => item.challengeId}
      ListHeaderComponent={renderHeader}
      ListFooterComponent={renderFooter}
      ListEmptyComponent={matchHistoryExpanded ? renderEmpty : null}
      contentContainerStyle={friendStatsStyles.listContent}
      showsVerticalScrollIndicator={false}
      testID={testID || 'pick-history-list'}
    />
  );
};


